import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { useQuery, useQueryClient } from 'react-query';
import api from '~/services/api';
import { toast } from 'react-toastify';
import { useLayout } from '../../Layout/LayoutContext';
import { NavigationContext } from '~/context/navigation';
import useAuth from '~/hooks/useAuth';
import icones from '~/config/icones';

export type ViewMode =
  | 'modules'
  | 'favorites'
  | 'recent'
  | 'most-accessed'
  | 'trails';

interface MenusContextData {
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  moreAccessed: any;
  recentes: any;
  trilhas: any[];
  setTrilhas: (data: any[]) => void;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  loadMoreAccessed: () => Promise<void>;
  loadRecentes: () => Promise<void>;
  loadTrilhas: () => Promise<void>;
  handleFavorite: (cod_tela: number, flg_favorita: any) => Promise<void>;
  handleAddAccessed: (cod_tela: number) => Promise<void>;
  // Propriedades de compatibilidade (deprecated)
  setMoreAccessed: (data: any) => void;
  setRecentes: (data: any) => void;
  reloadData: boolean;
  setReloadData: (reload: boolean) => void;
}

const MenusContext = createContext<MenusContextData>({} as MenusContextData);

interface MenusProviderProps {
  children: React.ReactNode;
}

export const MenusProvider: React.FC<MenusProviderProps> = ({ children }) => {
  const { setReloadTelas, reloadTelas } = useLayout();
  const { navmenu } = useContext(NavigationContext);
  const user = useAuth();
  const queryClient = useQueryClient();
  const [viewMode, setViewMode] = useState<ViewMode>('modules');
  const [searchTerm, setSearchTerm] = useState('');

  // Cache para telas mais acessadas - atualiza quando uma tela é acessada
  const { data: moreAccessed = null } = useQuery(
    ['most-accessed', user.user?.cod_controle],
    async () => {
      const { data } = await api.get('/most-accessed');
      if (data.success) {
        return {
          data: data.data,
          codes: data.codes,
        };
      }
      return null;
    },
    {
      enabled: !!user.user?.cod_controle && viewMode === 'most-accessed',
      staleTime: 1000 * 60 * 2, // 2 minutos (atualiza mais frequentemente)
      cacheTime: 1000 * 60 * 5, // 5 minutos
      refetchOnWindowFocus: false,
    },
  );

  // Cache para telas recentes - atualiza quando uma tela é acessada
  const { data: recentes = null } = useQuery(
    ['recentes', user.user?.cod_controle],
    async () => {
      const { data } = await api.get('/recentes');
      if (data.success && data.data.length > 0) {
        return {
          data: data.data,
          codes: data.codes,
        };
      }
      return null;
    },
    {
      enabled: !!user.user?.cod_controle && viewMode === 'recent',
      staleTime: 1000 * 60 * 2, // 2 minutos (atualiza mais frequentemente)
      cacheTime: 1000 * 60 * 5, // 5 minutos
      refetchOnWindowFocus: false,
    },
  );

  // Cache para trilhas - 15 minutos de cache (raramente mudam)
  const { data: trilhas = [] } = useQuery(
    ['trilhas', user.user?.cod_controle],
    async () => {
      if (!user.user?.cod_controle) {
        return [];
      }

      try {
        const { data } = await api.get(
          `/modulo-trilhas/${user.user.cod_controle}`,
        );

        if (!data.success) {
          return [];
        }

        // Criar mapeamento de URLs igual ao NavbarOld
        const urlTelas: any = {};
        navmenu.forEach((item: any) => {
          urlTelas[`${item.id}`] = item.url;
        });

        // Processar trilhas igual ao NavbarOld
        const trilhasProcessadas = data.data.map((trilha: any) => {
          // Processar ícone da trilha
          const { lib_icon, des_icon } = trilha;
          const iconkeyTrilha = Object.keys(icones).find(
            (icon) => icon === lib_icon,
          );
          let IconTrilha = '';
          if (iconkeyTrilha) {
            const Lib = icones[iconkeyTrilha];
            IconTrilha = Lib[des_icon];
          }
          trilha.Icon = IconTrilha;

          const telasTrilhas =
            trilha.des_json?.map((tela: any) => {
              // Vincular content através do navmenu
              const [filteredComponent] = navmenu.filter(
                (cpmnt: any) => cpmnt.id === tela.cod_tela,
              );
              if (filteredComponent) {
                tela.content = filteredComponent.content;
              }

              // Processar ícone da tela
              const { lib_icon: lib_icon_tela, des_icon: des_icon_tela } = tela;
              const iconkeyTela = Object.keys(icones).find(
                (icon) => icon === lib_icon_tela,
              );
              let IconTela = '';
              if (iconkeyTela) {
                const Lib = icones[iconkeyTela];
                IconTela = Lib[des_icon_tela];
              }
              tela.Icon = IconTela;

              // Processar URL igual ao NavbarOld
              if (urlTelas[`${tela.cod_tela}`]) {
                tela.url = urlTelas[`${tela.cod_tela}`];
              } else {
                tela.url = '/app';
              }

              return tela;
            }) || [];

          return {
            ...trilha,
            telas: telasTrilhas,
          };
        });

        return trilhasProcessadas;
      } catch (error) {
        console.error('Erro ao carregar trilhas:', error);
        return [];
      }
    },
    {
      enabled: !!user.user?.cod_controle && viewMode === 'trails',
      staleTime: 1000 * 60 * 15, // 15 minutos
      cacheTime: 1000 * 60 * 30, // 30 minutos
      refetchOnWindowFocus: false,
    },
  );

  // Funções de compatibilidade
  const noopWithParam = (..._args: any[]) => {}; // eslint-disable-line @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars

  return (
    <MenusContext.Provider
      value={{
        viewMode,
        setViewMode,
        moreAccessed,
        recentes,
        trilhas,
        setTrilhas: noopWithParam,
        searchTerm,
        setSearchTerm,
        loadMoreAccessed: () =>
          queryClient.invalidateQueries(['most-accessed']),
        loadRecentes: () => queryClient.invalidateQueries(['recentes']),
        loadTrilhas: () => queryClient.invalidateQueries(['trilhas']),
        handleFavorite: async (cod_tela: number, flg_favorita: any) => {
          try {
            const { data } = await api.post(`/favoritarTela/${cod_tela}`, {
              flg_favorita,
            });
            if (data.success) {
              queryClient.invalidateQueries(['telas-modulo']);
              setReloadTelas(!reloadTelas);
            }
          } catch (error) {
            toast.error('Erro ao favoritar a tela');
          }
        },
        handleAddAccessed: async (cod_tela: number) => {
          try {
            await api.post('/addMaisAcessada', { cod_tela });
            queryClient.invalidateQueries(['most-accessed']);
            queryClient.invalidateQueries(['recentes']);
          } catch (error: any) {
            console.error('Erro ao adicionar tela às mais acessadas:', error);
          }
        },
        // Propriedades de compatibilidade (deprecated)
        setMoreAccessed: noopWithParam,
        setRecentes: noopWithParam,
        reloadData: false,
        setReloadData: noopWithParam,
      }}
    >
      {children}
    </MenusContext.Provider>
  );
};

export const useMenus = (): MenusContextData => {
  const context = useContext(MenusContext);
  if (!context) {
    throw new Error('useMenus deve ser usado dentro de um MenusProvider');
  }
  return context;
};
